import { PDFGenerationOptions } from './pdf-types';

/**
 * Default PDF generation options
 */
export const DEFAULT_PDF_OPTIONS: PDFGenerationOptions = {
  format: 'a4',
  orientation: 'portrait',
  margin: {
    top: 20,
    right: 20,
    bottom: 20,
    left: 20,
  },
  quality: 0.95,
  scale: 2,
  useCORS: true,
  allowTaint: false,
};

/**
 * Sanitizes HTML content for PDF generation
 */
export const sanitizeHTMLForPDF = (html: string): string => {
  // Remove script tags and other potentially problematic elements
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
    .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '');
};

/**
 * Generates a safe filename for PDF export
 */
export const generatePDFFilename = (title: string, suffix?: string): string => {
  const sanitizedTitle = title
    .replace(/[^a-zA-Z0-9\s-_]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .toLowerCase()
    .substring(0, 50); // Limit length

  const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const suffixPart = suffix ? `-${suffix}` : '';
  
  return `${sanitizedTitle}${suffixPart}-${timestamp}.pdf`;
};

/**
 * Formats date for PDF display
 */
export const formatDateForPDF = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Converts markdown-like content to HTML for PDF rendering
 */
export const markdownToHTML = (markdown: string): string => {
  const styledHTML = `
    <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; max-width: 100%; padding: 20px;">
      ${markdown
        // Headers with styling
        .replace(/^### (.*$)/gim, '<h3 style="color: #374151; margin: 20px 0 10px 0; font-size: 18px; font-weight: 600;">$1</h3>')
        .replace(/^## (.*$)/gim, '<h2 style="color: #1f2937; margin: 25px 0 15px 0; font-size: 22px; font-weight: 700; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px;">$1</h2>')
        .replace(/^# (.*$)/gim, '<h1 style="color: #f97316; margin: 30px 0 20px 0; font-size: 28px; font-weight: 800; border-bottom: 3px solid #f97316; padding-bottom: 12px;">$1</h1>')
        // Bold and italic
        .replace(/\*\*(.*?)\*\*/gim, '<strong style="color: #1f2937; font-weight: 600;">$1</strong>')
        .replace(/\*(.*?)\*/gim, '<em style="font-style: italic;">$1</em>')
        // Links
        .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" style="color: #3b82f6; text-decoration: underline;">$1</a>')
        // Horizontal rules
        .replace(/^---$/gim, '<hr style="border: none; border-top: 2px solid #e5e7eb; margin: 30px 0;">')
        // Paragraphs and line breaks
        .replace(/\n\n/gim, '</p><p style="margin: 12px 0; line-height: 1.7;">')
        .replace(/\n/gim, '<br>')}
    </div>
  `;
  
  // Wrap in paragraph tags and clean up
  return styledHTML
    .replace(/(<div[^>]*>)/, '$1<p style="margin: 12px 0; line-height: 1.7;">')
    .replace(/(<\/div>)/, '</p>$1');
};

/**
 * Waits for images to load before PDF generation
 */
export const waitForImages = (element: HTMLElement): Promise<void> => {
  const images = element.querySelectorAll('img');
  const promises = Array.from(images).map((img) => {
    if (img.complete) return Promise.resolve();
    
    return new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => resolve(); // Continue even if image fails to load
      
      // Timeout after 5 seconds
      setTimeout(() => resolve(), 5000);
    });
  });
  
  return Promise.all(promises).then(() => {});
};

/**
 * Calculates optimal page dimensions based on content
 */
export const calculatePageDimensions = (
  format: 'a4' | 'letter' | 'legal',
  orientation: 'portrait' | 'landscape'
): { width: number; height: number } => {
  const dimensions = {
    a4: { width: 210, height: 297 }, // mm
    letter: { width: 216, height: 279 }, // mm
    legal: { width: 216, height: 356 }, // mm
  };

  const { width, height } = dimensions[format];
  
  return orientation === 'landscape' 
    ? { width: height, height: width }
    : { width, height };
};

/**
 * Estimates the number of pages needed for content
 */
export const estimatePageCount = (contentHeight: number, pageHeight: number, margin: number): number => {
  const usableHeight = pageHeight - (margin * 2);
  return Math.ceil(contentHeight / usableHeight);
};

/**
 * Creates a download link for the PDF blob
 */
export const downloadPDF = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}; 